import React, { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { QRCodeLogin } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { AuthPage } from './components/AuthPage';
import { AdminDashboard } from './components/AdminDashboard';
import { WhatsAppContact, WhatsAppChat } from './components/WhatsAppContacts';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getToken } from './api/auth';
import { LogOut, Shield, Loader2, User } from 'lucide-react';
import { SOCKET_URL } from './config';

// Create socket with autoConnect disabled so we can add listeners before connecting
export const socket: Socket = io(SOCKET_URL, { autoConnect: false });

export type Platform = 'whatsapp' | 'signal';

export interface ConnectionState {
    whatsapp: boolean;
    whatsappSyncing: boolean;
    whatsappSyncProgress: {
        contacts: boolean;
        chats: boolean;
        messages: boolean;
    };
    signal: boolean;
    signalNumber: string | null;
    signalApiAvailable: boolean;
    signalQrImage: string | null;
    whatsappQr: string | null;
}

function AppContent() {
    const { user, isLoading: authLoading, isAuthenticated, isAdmin, logout } = useAuth();
    const [isConnected, setIsConnected] = useState(socket.connected);
    const [isSocketAuthenticated, setIsSocketAuthenticated] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        whatsapp: false,
        whatsappSyncing: false,
        whatsappSyncProgress: {
            contacts: false,
            chats: false,
            messages: false
        },
        signal: false,
        signalNumber: null,
        signalApiAvailable: false,
        signalQrImage: null,
        whatsappQr: null
    });
    const [whatsappContacts, setWhatsappContacts] = useState<WhatsAppContact[]>([]);
    const [whatsappChats, setWhatsappChats] = useState<WhatsAppChat[]>([]);
    const [whatsappMessageCounts, setWhatsappMessageCounts] = useState<{ [key: string]: number }>({});

    const isAnyPlatformReady = connectionState.whatsapp || connectionState.signal;

    useEffect(() => {
        function onConnect() {
            setIsConnected(true);
            // Authenticate socket when connected
            const token = getToken();
            if (token) {
                socket.emit('authenticate', token);
            }
        }

        function onDisconnect() {
            setIsConnected(false);
            setIsSocketAuthenticated(false);
            setConnectionState({
                whatsapp: false,
                whatsappSyncing: false,
                whatsappSyncProgress: {
                    contacts: false,
                    chats: false,
                    messages: false
                },
                signal: false,
                signalNumber: null,
                signalApiAvailable: false,
                signalQrImage: null,
                whatsappQr: null
            });
        }

        function onAuthenticated(data: { success: boolean }) {
            setIsSocketAuthenticated(data.success);
        }

        function onWhatsAppConnectionOpen() {
            console.log('[WHATSAPP] Connection opened');
            // Don't set syncing to true here - let the server tell us if we need to sync
            // For returning sessions, sync-complete will be sent immediately
            setConnectionState(prev => ({ ...prev, whatsapp: true, whatsappQr: null }));
        }

        function onWhatsAppConnectionClose() {
            console.log('[WHATSAPP] Connection closed');
            setConnectionState(prev => ({ ...prev, whatsapp: false, whatsappQr: null, whatsappSyncing: false }));
        }

        function onWhatsAppDisconnected() {
            console.log('[WHATSAPP] Disconnected (logged out)');
            setConnectionState(prev => ({ 
                ...prev, 
                whatsapp: false, 
                whatsappQr: null,
                whatsappSyncing: false,
                whatsappSyncProgress: { contacts: false, chats: false, messages: false }
            }));
            setWhatsappContacts([]);
            setWhatsappChats([]);
            setWhatsappMessageCounts({});
        }

        function onWhatsAppSyncing(data: { syncing: boolean; progress: { contacts: boolean; chats: boolean; messages: boolean } }) {
            console.log('[WHATSAPP] Syncing:', data);
            setConnectionState(prev => ({ 
                ...prev, 
                whatsappSyncing: data.syncing,
                whatsappSyncProgress: data.progress
            }));
        }

        function onWhatsAppSyncComplete() {
            console.log('[WHATSAPP] Sync complete');
            setConnectionState(prev => ({ 
                ...prev, 
                whatsappSyncing: false,
                whatsappSyncProgress: { contacts: true, chats: true, messages: true }
            }));
        }

        function onWhatsAppQr(qr: string) {
            console.log('[WHATSAPP] Received QR code');
            setConnectionState(prev => ({ ...prev, whatsappQr: qr }));
        }

        function onSignalConnectionOpen(data: { number: string }) {
            setConnectionState(prev => ({
                ...prev,
                signal: true,
                signalNumber: data.number
            }));
        }

        function onSignalDisconnected() {
            setConnectionState(prev => ({
                ...prev,
                signal: false,
                signalNumber: null
            }));
        }

        function onSignalApiStatus(data: { available: boolean }) {
            setConnectionState(prev => ({ ...prev, signalApiAvailable: data.available }));
        }

        function onSignalQrImage(url: string) {
            console.log('[SIGNAL] Received QR image URL:', url);
            setConnectionState(prev => ({ ...prev, signalQrImage: url }));
        }

        function onWhatsAppContacts(contacts: WhatsAppContact[]) {
            console.log('[WHATSAPP] Received contacts:', contacts.length);
            setWhatsappContacts(contacts);
        }

        function onWhatsAppChats(chats: WhatsAppChat[]) {
            console.log('[WHATSAPP] Received chats:', chats.length);
            setWhatsappChats(chats);
        }

        function onWhatsAppMessageCounts(counts: { [key: string]: number }) {
            console.log('[WHATSAPP] Received message counts:', Object.keys(counts).length, 'chats');
            setWhatsappMessageCounts(counts);
        }

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('authenticated', onAuthenticated);
        socket.on('qr', onWhatsAppQr);
        socket.on('connection-open', onWhatsAppConnectionOpen);
        socket.on('connection-close', onWhatsAppConnectionClose);
        socket.on('whatsapp-disconnected', onWhatsAppDisconnected);
        socket.on('whatsapp-syncing', onWhatsAppSyncing);
        socket.on('whatsapp-sync-complete', onWhatsAppSyncComplete);
        socket.on('signal-connection-open', onSignalConnectionOpen);
        socket.on('signal-disconnected', onSignalDisconnected);
        socket.on('signal-api-status', onSignalApiStatus);
        socket.on('signal-qr-image', onSignalQrImage);
        socket.on('whatsapp-contacts', onWhatsAppContacts);
        socket.on('whatsapp-chats', onWhatsAppChats);
        socket.on('whatsapp-message-counts', onWhatsAppMessageCounts);

        // Connect socket when user is authenticated
        if (isAuthenticated && !socket.connected) {
            socket.connect();
        }

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('authenticated', onAuthenticated);
            socket.off('qr', onWhatsAppQr);
            socket.off('connection-open', onWhatsAppConnectionOpen);
            socket.off('connection-close', onWhatsAppConnectionClose);
            socket.off('whatsapp-disconnected', onWhatsAppDisconnected);
            socket.off('whatsapp-syncing', onWhatsAppSyncing);
            socket.off('whatsapp-sync-complete', onWhatsAppSyncComplete);
            socket.off('signal-connection-open', onSignalConnectionOpen);
            socket.off('signal-disconnected', onSignalDisconnected);
            socket.off('signal-api-status', onSignalApiStatus);
            socket.off('signal-qr-image', onSignalQrImage);
            socket.off('whatsapp-contacts', onWhatsAppContacts);
            socket.off('whatsapp-chats', onWhatsAppChats);
            socket.off('whatsapp-message-counts', onWhatsAppMessageCounts);
        };
    }, [isAuthenticated]);

    // Re-authenticate socket when user logs in
    useEffect(() => {
        if (isAuthenticated && socket.connected) {
            const token = getToken();
            if (token) {
                socket.emit('authenticate', token);
            }
        }
    }, [isAuthenticated]);

    const handleLogout = async () => {
        socket.disconnect();
        setIsSocketAuthenticated(false);
        await logout();
    };

    // Show loading while checking auth
    if (authLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    // Show login page if not authenticated
    if (!isAuthenticated) {
        return <AuthPage />;
    }

    // Show admin dashboard if requested
    if (showAdmin && isAdmin) {
        return <AdminDashboard onBack={() => setShowAdmin(false)} />;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <img 
                                src="/logo192.png" 
                                alt="WhatSignal Tracker" 
                                className="w-10 h-10 rounded-xl"
                            />
                            WhatSignal Tracker
                        </h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Connection Status */}
                        <div className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`} />
                            <span className="text-sm text-gray-300">{isConnected ? 'Server Connected' : 'Disconnected'}</span>
                            {isConnected && (
                                <>
                                    <div className="w-px h-4 bg-white/20" />
                                    <div className={`w-2.5 h-2.5 rounded-full ${connectionState.whatsapp ? 'bg-green-400' : 'bg-yellow-400'}`} />
                                    <span className="text-sm text-gray-300">WA</span>
                                    <div className={`w-2.5 h-2.5 rounded-full ${connectionState.signal ? 'bg-green-400' : 'bg-yellow-400'}`} />
                                    <span className="text-sm text-gray-300">Signal</span>
                                </>
                            )}
                        </div>

                        {/* User Info & Actions */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-white font-medium">{user?.username}</span>
                                {isAdmin && (
                                    <span className="px-2 py-0.5 bg-purple-500/30 text-purple-300 text-xs rounded-full">Admin</span>
                                )}
                            </div>
                            
                            {isAdmin && (
                                <button
                                    onClick={() => setShowAdmin(true)}
                                    className="p-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-purple-400 transition-all"
                                    title="Admin Dashboard"
                                >
                                    <Shield className="w-5 h-5" />
                                </button>
                            )}
                            
                            <button
                                onClick={handleLogout}
                                className="p-2.5 bg-white/10 hover:bg-red-500/20 rounded-xl text-gray-400 hover:text-red-400 transition-all"
                                title="Logout"
                            >
                                <LogOut className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </header>

                <main>
                    {!isAnyPlatformReady ? (
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-semibold text-white mb-2">Connect Your Messaging Apps</h2>
                                <p className="text-gray-400">Scan the QR codes below to link your WhatsApp and/or Signal accounts</p>
                            </div>
                            <QRCodeLogin 
                                connectionState={connectionState} 
                                whatsappContacts={whatsappContacts}
                                whatsappChats={whatsappChats}
                            />
                        </div>
                    ) : connectionState.whatsapp && connectionState.whatsappSyncing ? (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-12 text-center">
                                <div className="mb-6">
                                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center mb-4">
                                        <Loader2 className="w-10 h-10 text-white animate-spin" />
                                    </div>
                                    <h2 className="text-2xl font-semibold text-white mb-2">Syncing WhatsApp Data</h2>
                                    <p className="text-gray-400">Please wait while we sync your contacts, chats, and messages...</p>
                                </div>
                                
                                <div className="space-y-3 max-w-xs mx-auto">
                                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                                        <span className="text-gray-300">Contacts</span>
                                        {connectionState.whatsappSyncProgress.contacts ? (
                                            <span className="text-green-400 font-medium">✓ Synced</span>
                                        ) : (
                                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                                        <span className="text-gray-300">Chats</span>
                                        {connectionState.whatsappSyncProgress.chats ? (
                                            <span className="text-green-400 font-medium">✓ Synced</span>
                                        ) : (
                                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                                        <span className="text-gray-300">Messages</span>
                                        {connectionState.whatsappSyncProgress.messages ? (
                                            <span className="text-green-400 font-medium">✓ Synced</span>
                                        ) : (
                                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                        )}
                                    </div>
                                </div>
                                
                                <p className="text-xs text-gray-500 mt-6">This may take a few moments depending on your chat history</p>
                            </div>
                        </div>
                    ) : (
                        <Dashboard 
                            connectionState={connectionState}
                            whatsappContacts={whatsappContacts}
                            whatsappChats={whatsappChats}
                            whatsappMessageCounts={whatsappMessageCounts}
                        />
                    )}
                </main>
            </div>
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
